import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import config, { SERVER_URL } from "@/config/config";
import { SlotMachineBetStatus } from "@/constants/enum/slot";
import { approveGameCash } from "@/helper/contractFunctions/MafiaGameBank";
import {
  depositLiquidity,
  finishBet,
  getSlotMachines,
  getUserBetInfo,
  initializeBet,
  removeLiquidity,
  updateSlotMachineSettings,
} from "@/helper/contractFunctions/MafiaSlotMachine";
import { SlotMachine } from "@/types/Contract/SlotMachine/SlotMachine";
import { SlotMachineBetHistory } from "@/types/SlotMachineBetHistory";
import { getSlotMachineLiquidity } from "@/helper/contractFunctions/MafiaSlotMachine";
import { SlotBetResult } from "@/types/SlotBetResult";

export interface SlotState {
  currentSlotMachineId: number;
  currentSlotMachineLiquidity: number;

  isSlotMachineListLoading: boolean;
  isInitializingBet: boolean;
  isFinishingBet: boolean;
  isUpdatingSlotMachineSettings: boolean;
  isGettingSlotMachineLiquidity: boolean;
  isAddingLiquidity: boolean;
  isRemovingLiquidity: boolean;
  isApprovingGameCash: boolean;
  isVrfPending: boolean;

  defaultBetAmount: number;
  amountPerSpin: number;
  spinCount: number;
  currentSpinIndex: number;

  betDisabled: boolean;
  betStatus: SlotMachineBetStatus;
  slotMachines: SlotMachine[];

  tableHistory: SlotMachineBetHistory[];
  myHistory: SlotMachineBetHistory[];

  betResult?: SlotBetResult;
}

const initialState: SlotState = {
  currentSlotMachineId: -1,
  currentSlotMachineLiquidity: 0,

  isSlotMachineListLoading: false,
  isInitializingBet: false,
  isFinishingBet: false,
  isUpdatingSlotMachineSettings: false,
  isGettingSlotMachineLiquidity: false,
  isAddingLiquidity: false,
  isRemovingLiquidity: false,
  isApprovingGameCash: false,
  isVrfPending: false,

  defaultBetAmount: 0,
  amountPerSpin: 0,
  spinCount: 1,
  currentSpinIndex: 0,

  betDisabled: false,
  betStatus: SlotMachineBetStatus.NOT_REQUESTED,
  slotMachines: [],

  tableHistory: [],
  myHistory: [],
};

export const getSlotMachinesAction = createAsyncThunk(
  "slot/getSlotMachinesAction",
  async () => {
    const slotMachines = await getSlotMachines();

    return slotMachines;
  }
);
export const getUserBetInfoAction = createAsyncThunk(
  "slot/getUserBetInfoAction",
  async ({
    account,
    slotMachineId,
  }: {
    account: string;
    slotMachineId: number;
  }) => {
    const userBetInfo = await getUserBetInfo(account, slotMachineId);

    return userBetInfo;
  }
);

export const initializeBetAction = createAsyncThunk(
  "slot/initializeBetAction",
  async ({
    account,
    slotMachineId,
    spinCount,
    amountPerSpin,
    signature,
    message,
  }: {
    account: string;
    slotMachineId: number;
    spinCount: number;
    amountPerSpin: number;
    signature: string;
    message: string;
  }) => {
    const data = await initializeBet(
      account,
      slotMachineId,
      spinCount,
      amountPerSpin,
      signature,
      message
    );

    return data.transactionHash;
  }
);

export const finishBetAction = createAsyncThunk(
  "slot/finishBetAction",
  async ({
    account,
    slotMachineId,
  }: {
    account: string;
    slotMachineId: number;
  }) => {
    const data = await finishBet(account, slotMachineId);

    return data.transactionHash;
  }
);

export const updateSlotMachineSettingsAction = createAsyncThunk(
  "slot/updateSlotMachineSettingsAction",
  async ({
    account,
    slotMachineId,
    // isOpened,
    minBet,
    maxBet,
    message,
    signature,
  }: {
    account: string;
    slotMachineId: number;
    // isOpened: boolean;
    minBet: number;
    maxBet: number;
    message: string;
    signature: string;
  }) => {
    const data = await updateSlotMachineSettings(
      account,
      slotMachineId,
      // isOpened,
      minBet,
      maxBet,
      message,
      signature
    );

    return data.transactionHash;
  }
);

export const getSlotMachineLiquidityAction = createAsyncThunk(
  "slot/getSlotMachineLiquidityAction",
  async ({
    slotMachineId,
    message,
    signature,
  }: {
    slotMachineId: number;
    message: string;
    signature: string;
  }) => {
    const liquidity = await getSlotMachineLiquidity(
      slotMachineId,
      message,
      signature
    );
    return Number(liquidity);
  }
);

export const addLiquidityAction = createAsyncThunk(
  "slot/addLiquidityAction",
  async ({
    account,
    slotMachineId,
    amount,
  }: {
    account: string;
    slotMachineId: number;
    amount: number;
  }) => {
    const data = await depositLiquidity(account, slotMachineId, amount);

    return data.transactionHash;
  }
);

export const removeLiquidityAction = createAsyncThunk(
  "slot/removeLiquidityAction",
  async ({
    account,
    slotMachineId,
    amount,
  }: {
    account: string;
    slotMachineId: number;
    amount: number;
  }) => {
    const data = await removeLiquidity(account, slotMachineId, amount);

    return data.transactionHash;
  }
);

export const approveGameCashAction = createAsyncThunk(
  "slot/approveGameCashAction",
  async ({ cashAmount, account }: { cashAmount: number; account: string }) => {
    const data = await approveGameCash(cashAmount, config.slotAddress, account);

    return data.transactionHash || "";
  }
);

export const getTableHistoryAction = createAsyncThunk(
  "slot/getTableHistoryAction",
  async ({ id }: { id: number }) => {
    const response = await axios.get(
      `${SERVER_URL}/business/slotmachine/${id}`
    );

    return response.data;
  }
);

export const getMyHistoryAction = createAsyncThunk(
  "slot/getMyHistoryAction",
  async ({ account }: { account: string }) => {
    const response = await axios.get(
      // `${SERVER_URL}/business/slotmachine/history/0xaD3f2043D1C1F42427F4Ca893B637F629647ae16`
      `${SERVER_URL}/business/slotmachine/history/${account}`
    );

    return response.data;
  }
);

export const slotSlice = createSlice({
  name: "slot",
  initialState,
  reducers: {
    setCurrentSlotMachineId: (state, action) => {
      state.currentSlotMachineId = action.payload;
    },

    setAmountPerSpin: (state, { payload }) => {
      state.amountPerSpin = payload;
    },

    setSpinCount: (state, { payload }) => {
      state.spinCount = payload;
    },

    updateVrfPending: (state, { payload }) => {
      state.isVrfPending = payload;
    },

    updateBetDisabled: (state, { payload }) => {
      state.betDisabled = payload;
    },

    updateBetStatus: (state, { payload }) => {
      state.betStatus = payload;
    },
    updateDefaultBetAmount: (state, { payload }) => {
      state.defaultBetAmount = Number(payload);
      state.amountPerSpin = Number(payload);
    },
    setBetResult: (state, { payload }) => {
      state.betResult = payload;
    },

    updateCurrentSpinIndex: (state, { payload }) => {
      state.currentSpinIndex = payload;
    },

    clearBetResult: (state) => {
      state.betResult = undefined;
    },
  },
  extraReducers(builder) {
    builder.addCase(getSlotMachinesAction.pending, (state) => {
      state.isSlotMachineListLoading = true;
    });
    builder.addCase(getSlotMachinesAction.fulfilled, (state, action) => {
      state.slotMachines = action.payload;
      state.isSlotMachineListLoading = false;
    });
    builder.addCase(getSlotMachinesAction.rejected, (state) => {
      state.isSlotMachineListLoading = false;
    });

    builder.addCase(initializeBetAction.pending, (state) => {
      state.isInitializingBet = true;
    });
    builder.addCase(initializeBetAction.fulfilled, (state) => {
      state.isInitializingBet = false;
    });
    builder.addCase(initializeBetAction.rejected, (state) => {
      state.isInitializingBet = false;
    });

    builder.addCase(finishBetAction.pending, (state) => {
      state.isFinishingBet = true;
    });
    builder.addCase(finishBetAction.fulfilled, (state) => {
      state.isFinishingBet = false;
    });
    builder.addCase(finishBetAction.rejected, (state) => {
      state.isFinishingBet = false;
    });

    builder.addCase(updateSlotMachineSettingsAction.pending, (state) => {
      state.isUpdatingSlotMachineSettings = true;
    });
    builder.addCase(updateSlotMachineSettingsAction.fulfilled, (state) => {
      state.isUpdatingSlotMachineSettings = false;
    });
    builder.addCase(updateSlotMachineSettingsAction.rejected, (state) => {
      state.isUpdatingSlotMachineSettings = false;
    });

    builder.addCase(getSlotMachineLiquidityAction.pending, (state) => {
      state.isGettingSlotMachineLiquidity = true;
    });
    builder.addCase(
      getSlotMachineLiquidityAction.fulfilled,
      (state, { payload }) => {
        state.isGettingSlotMachineLiquidity = false;
        state.currentSlotMachineLiquidity = payload;
      }
    );
    builder.addCase(
      getSlotMachineLiquidityAction.rejected,
      (state, { error }) => {
        state.isGettingSlotMachineLiquidity = false;

        console.log(error);
      }
    );

    builder.addCase(addLiquidityAction.pending, (state) => {
      state.isAddingLiquidity = true;
    });
    builder.addCase(addLiquidityAction.fulfilled, (state) => {
      state.isAddingLiquidity = false;
    });
    builder.addCase(addLiquidityAction.rejected, (state) => {
      state.isAddingLiquidity = false;
    });

    builder.addCase(removeLiquidityAction.pending, (state) => {
      state.isRemovingLiquidity = true;
    });
    builder.addCase(removeLiquidityAction.fulfilled, (state) => {
      state.isRemovingLiquidity = false;
    });
    builder.addCase(removeLiquidityAction.rejected, (state) => {
      state.isRemovingLiquidity = false;
    });

    builder.addCase(approveGameCashAction.pending, (state) => {
      state.isApprovingGameCash = true;
    });
    builder.addCase(approveGameCashAction.fulfilled, (state) => {
      state.isApprovingGameCash = false;
    });
    builder.addCase(approveGameCashAction.rejected, (state) => {
      state.isApprovingGameCash = false;
    });

    builder.addCase(getMyHistoryAction.pending, (state) => {});
    builder.addCase(getMyHistoryAction.fulfilled, (state, { payload }) => {
      state.myHistory = payload.map((item: any) => ({
        id: item.id,
        slotMachineId: item.slotMachineId,
        player: item.player,
        nonce: JSON.parse(JSON.parse(item.nonce)),
        spinCount: item.spinCount,
        amountPerSpin: item.amountPerSpin,
        totalReward: item.totalReward,
        rewardReceived: item.rewardReceived,
        feeAmount: item.feeAmount,
        timestamp: item.timestamp,
        block: item.block,
        user: {
          name: item.User.name,
          imageId: item.User.imageId,
          city: item.User.city,
        },
      })) as SlotMachineBetHistory[];
    });
    builder.addCase(getMyHistoryAction.rejected, (state) => {});

    builder.addCase(getTableHistoryAction.pending, (state) => {});
    builder.addCase(getTableHistoryAction.fulfilled, (state, { payload }) => {
      state.tableHistory = payload.map((item: any) => ({
        id: item.id,
        slotMachineId: item.slotMachineId,
        player: item.player,
        nonce: JSON.parse(JSON.parse(item.nonce)),
        spinCount: item.spinCount,
        amountPerSpin: item.amountPerSpin,
        totalReward: item.totalReward,
        rewardReceived: item.rewardReceived,
        feeAmount: item.feeAmount,
        timestamp: item.timestamp,
        block: item.block,
        user: {
          name: item.User.name,
          imageId: item.User.imageId,
          city: item.User.city,
        },
      })) as SlotMachineBetHistory[];
    });
    builder.addCase(getTableHistoryAction.rejected, (state) => {});
  },
});

export const {
  setCurrentSlotMachineId,
  setAmountPerSpin,
  setSpinCount,
  setBetResult,
  clearBetResult,
  updateBetDisabled,
  updateBetStatus,
  updateDefaultBetAmount,
  updateCurrentSpinIndex,
  updateVrfPending,
} = slotSlice.actions;

export default slotSlice.reducer;
